import React from 'react';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileTransfer } from '@capacitor/file-transfer';
import { FileOpener } from '@capacitor-community/file-opener';
import toast from 'react-hot-toast';
import { trackEvent, MixpanelEventName } from 'common/utils/mixpanel/eventTriggers';

// Strip anything that could break a filesystem path or hide the extension.
const safeFileName = (fileName: string): string => {
  const cleaned = (fileName || 'document')
    .replace(/[/\\:*?"<>|]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned || 'document';
};

// Best-effort MIME from extension so iOS opens the file with the right
// viewer (Quick Look handles these natively). Exported so renewalCalendar.ts
// can reuse the same extension -> MIME contract for its .ics deliveries
// instead of carrying a second copy of this map.
export const mimeFor = (fileName: string): string | undefined => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  const map: Record<string, string> = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    heic: 'image/heic',
    ics: 'text/calendar',
  };
  return ext ? map[ext] : undefined;
};

// Pick a non-colliding name in the Documents directory: name.ext,
// name (1).ext, name (2).ext ... so re-downloads never fail or silently
// clobber a previous file.
const uniqueDocumentsPath = async (fileName: string): Promise<string> => {
  const dot = fileName.lastIndexOf('.');
  const base = dot > 0 ? fileName.slice(0, dot) : fileName;
  const ext = dot > 0 ? fileName.slice(dot) : '';
  let candidate = fileName;
  for (let i = 1; i <= 50; i += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await Filesystem.stat({ directory: Directory.Documents, path: candidate });
      candidate = `${base} (${i})${ext}`; // exists - try the next suffix
    } catch {
      return candidate; // stat threw => does not exist => free to use
    }
  }
  return `${base}-${Date.now()}${ext}`;
};

const saveOnWeb = async (url: string, fileName: string): Promise<void> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    link.click();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

// Returns the local file path so the toast's Open action can launch it.
const saveOnNative = async (url: string, fileName: string): Promise<string> => {
  const path = await uniqueDocumentsPath(fileName);
  const { uri } = await Filesystem.getUri({ directory: Directory.Documents, path });
  await FileTransfer.downloadFile({ url, path: uri });
  return uri;
};

const openSavedFile = async (filePath: string, fileName: string): Promise<void> => {
  try {
    await FileOpener.open({ filePath, contentType: mimeFor(fileName), openWithDefault: true });
  } catch (error) {
    console.warn('openSavedFile failed:', error);
    toast.error("Couldn't open the file.");
  }
};

// Success toast with an inline "Open" action that launches the file.
const savedToast = (filePath: string, fileName: string): void => {
  const where = Capacitor.getPlatform() === 'ios' ? 'Files › Doqit' : 'your files';
  const label = `Saved to ${where}`;
  toast.success(
    (t) => (
      <span className="flex items-center gap-3">
        <span>{label}</span>
        <button
          type="button"
          className="px-3 py-1 rounded-full bg-doqit-button-primary text-white text-sm font-medium"
          onClick={() => {
            toast.dismiss(t.id);
            openSavedFile(filePath, fileName);
          }}
        >
          Open
        </button>
      </span>
    ),
    { id: 'saveFile', duration: 6000 },
  );
};

/**
 * Download a document file directly to the device (Pattern 1).
 * Web: direct browser download via a same-origin blob URL.
 * Native: streams into the app Documents dir (Files > Doqit on iOS), then
 * shows a toast with an "Open" action that launches the file.
 * Never navigates/bounces on failure - shows a toast and returns.
 */
export const saveFile = async (url: string, fileName: string): Promise<void> => {
  // Every download button in the app funnels through here - one call covers
  // the whole surface (869edcw81).
  const fileType = fileName.split('.').pop()?.toLowerCase() || 'unknown';
  trackEvent(MixpanelEventName.documentDownloaded, { file_type: fileType });
  if (!url) return;
  const name = safeFileName(fileName);

  if (!Capacitor.isNativePlatform()) {
    try {
      await saveOnWeb(url, name);
    } catch (error) {
      console.warn('saveFile (web) failed:', error);
      trackEvent(MixpanelEventName.documentDownloadFailed, { platform: 'web', file_type: fileType });
      toast.error("Couldn't download - please try again.");
    }
    return;
  }

  toast.loading('Saving...', { id: 'saveFile' });
  try {
    const filePath = await saveOnNative(url, name);
    savedToast(filePath, name);
  } catch (error) {
    console.warn('saveFile (native) failed:', error);
    trackEvent(MixpanelEventName.documentDownloadFailed, { platform: 'native', file_type: fileType });
    toast.error("Couldn't download - please try again.", { id: 'saveFile' });
  }
};
